import { Modal, ModalBody, ModalFooter, ModalHeader } from '@windmill/react-ui';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSetSupervisorForLoggedInUserMutation } from 'src/components/StudentPage/queries';
import ApiErrorAlert from 'src/components/_common/ApiErrorAlert/ApiErrorAlert';
import Button from 'src/components/_common/Button/Button';
import useUser from 'src/hooks/user/useUser';
import { StudentUserReferences } from 'src/models/user';

interface SupervisorConfirmationModalProps {
  isOpen: boolean;
  close: () => void;
  supervisor: StudentUserReferences;
  classId: number;
}

const COMPONENT_ID = 'supervisor-confirmation-modal';

export default function SupervisorConfirmationModal({
  isOpen,
  close,
  supervisor,
  classId,
}: SupervisorConfirmationModalProps) {
  const { formatMessage } = useIntl();
  const mutation = useSetSupervisorForLoggedInUserMutation(onSuccess);
  const supervisorQuery = useUser(supervisor.userId);

  function onSubmit() {
    mutation.mutate({
      classId,
      supervisorUserId: supervisor.userId,
    });
  }

  function onSuccess() {
    close();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      id={COMPONENT_ID}
      style={{ margin: 'auto' }}
    >
      <ModalHeader className="mb-4" data-testid="modal-header">
        <FormattedMessage id="set-primary-supervisor" />
      </ModalHeader>
      <ModalBody>
        <p>
          {formatMessage(
            { id: 'supervisor-confirmation-message' },
            { supervisor: supervisorQuery.data?.displayName },
          )}
        </p>
        {mutation.isError && (
          <ApiErrorAlert
            error={mutation.error}
            message={formatMessage({ id: 'setSupervisorErrorMessage' })}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={close}
          text={formatMessage({ id: 'cancel' })}
          type="button"
          className="mr-2"
          data-testid="cancel-button"
        />
        <Button
          onClick={onSubmit}
          text={formatMessage({ id: 'confirm' })}
          isSaving={mutation.isLoading || supervisorQuery.isLoading}
          type="button"
          data-testid="confirm-button"
        />
      </ModalFooter>
    </Modal>
  );
}
